import {GeneralSaleTransactionType, TrustStatus} from '@sollapay/enums';
import {Money} from '@sollapay/utils';

import {TRUST_PURPOSE_CONFIG} from '../config';

import type {TrustPurpose} from '@sollapay/enums';
import type {TrustAccountListLedgerDTO} from '@sollapay/types';
import type {TFunction} from 'i18next';

/** Only an active account's balance is shown or sorted on; anything else reads as "no balance". */
export const getActiveBalance = (
  trustStatus: TrustStatus,
  balance: string | null | undefined
): Money | null =>
  trustStatus === TrustStatus.TRUST_ACTIVE && balance != null ? Money.from(balance) : null;

export const getBalanceAmount = (
  trustStatus: TrustStatus,
  balance: string | null | undefined
): number | null => getActiveBalance(trustStatus, balance)?.toNumber() ?? null;

export const getAccountBalanceAmount = (
  accountId: string,
  trustStatus: TrustStatus,
  balancesMap?: Record<string, TrustAccountListLedgerDTO>
): number | null => getBalanceAmount(trustStatus, balancesMap?.[accountId]?.balance);

export const displayOrNA = (value: string | null | undefined, t: TFunction): string =>
  value || t('common:na');

export const getTrustTypeLabel = (
  trustPurpose: TrustPurpose | null | undefined,
  t: TFunction
): string | undefined => {
  if (!trustPurpose) return undefined;
  return t(TRUST_PURPOSE_CONFIG[trustPurpose].labelKey);
};

const TRANSACTION_TYPE_LABEL_KEYS: Record<GeneralSaleTransactionType, string> = {
  [GeneralSaleTransactionType.SECOND_HAND_SALE]:
    'activation.packages.trustDetails.form.options.transactionTypes.secondHandSale',
  [GeneralSaleTransactionType.OPTION_ASSIGNMENT]:
    'activation.packages.trustDetails.form.options.transactionTypes.optionAssignment'
};

/** The deal shape beneath "general sale" — absent until the account has answered that question. */
export const getTrustTypeSubtitle = (
  transactionType: GeneralSaleTransactionType | null | undefined,
  t: TFunction
): string | undefined => {
  if (!transactionType) return undefined;
  return t(TRANSACTION_TYPE_LABEL_KEYS[transactionType]);
};

const IN_REVIEW_STATUSES: readonly TrustStatus[] = [
  TrustStatus.PENDING_VALIDATION,
  TrustStatus.IN_REVIEW,
  TrustStatus.ACTIVATION_PENDING
];

/** The account's call to action, a key in the `common` namespace; read-only callers get "view only". */
export const getTrustAccountActionLabelKey = (
  trustStatus: TrustStatus,
  canManage: boolean
): string => {
  if (!canManage) return 'trustAccounts.viewOnly';
  if (trustStatus === TrustStatus.TRUST_ACTIVE) return 'trustAccounts.manageBuyers';
  return IN_REVIEW_STATUSES.includes(trustStatus)
    ? 'trustAccounts.viewProgress'
    : 'trustAccounts.continueSetup';
};

export const getTrustTypeDisplay = (
  trustPurpose: TrustPurpose | null | undefined,
  generalSaleTransactionType: GeneralSaleTransactionType | null | undefined,
  t: TFunction
): {label: string; subtitle: string | undefined} => ({
  label: displayOrNA(getTrustTypeLabel(trustPurpose, t), t),
  subtitle: getTrustTypeSubtitle(generalSaleTransactionType, t)
});
